import {
  ArrowLeftIcon,
  ClipboardDocumentListIcon,
  DocumentMagnifyingGlassIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { getImportSourceBadgeMode, getImportSourceLabel, IMPORT_SOURCES } from '../../../@types/ImportSource';
import type { SciManualTreatment } from '../../../@types/SciContactImport';
import { isSciAnalysis, isSciReport } from '../../../@types/SciContactImport';
import type { RootState } from '../../../store/store';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import { showToast } from '../../common/Notification';
import NotLatestTestImport from '../../common/NotLatestTestImport';
import Spinner from '../../common/Spinner';
import ComparisonResultDisplay from '../components/ComparisonResultDisplay';
import type { ImportData } from '../components/ImportResultDisplay';
import { ImportResultBody } from '../components/ImportResultDisplay';
import ManualTreatmentTab from './ManualTreatmentTab.module';
import SciImportResult from './SciImportResult';
import SciImportReview from './SciImportReview';

interface Company {
  id: number;
  name: string;
  siren: string;
}

interface User {
  id: number;
  firstname: string;
  lastname: string;
}

interface ImportDataSummary {
  total?: number;
  inserted?: Record<string, unknown>;
  updated?: Record<string, unknown>;
  errors?: Record<string, unknown>;
  summary?: Record<string, unknown>;
  coprosOk?: unknown;
}

interface Import {
  id: number;
  type: number;
  data: ImportDataSummary | null;
  company: Company;
  createdBy: User;
  createdAt: string;
  isTestImport: boolean;
  importSource: string | null;
  manualTreatment: ManualTreatment | null;
  originalFileName: string | null;
}

export interface MatchedPair {
  fileEntry: { name: string; immatriculation: string; originalImmatriculation?: string };
  rnicEntry: { name: string; immatriculation: string };
  status: 'validated' | 'rejected' | 'pending';
  confidenceScore?: number;
  isManuallyLinked?: boolean; // True when user manually corrected immatriculation to create this pair
  matchSource?: 'name' | 'address' | 'manual';
}

export interface NotInRnicDecision {
  immatriculation: string;
  name: string;
  decision: 'force' | 'skip' | 'pending';
  reason?: string;
  correctedImmatriculation?: string;
  /** Type of copro when forced (1=Copropriété, 2=ASL, 3=Tertiaire, 4=AFUL, 5=Autre) */
  forcedType?: number;
}

export interface RnicNotInFileDecision {
  immatriculation: string;
  name: string;
  decision: 'ignore' | 'integrate' | 'pending';
}

export interface SyndicFeedbackOverride {
  key: string;
  pdfPoint: 1 | 2 | 3 | 4 | 5;
  originalType?: number;
  newType?: number;
  originalImmat?: string;
  newImmat?: string;
}

export interface DbCoproNotInFileDecision {
  coproId: number;
  immatriculation: string;
  name: string;
  type: number | null;
  decision: 'deactivate' | 'keep' | 'pending';
}

export interface ManualTreatment {
  matchedPairs: MatchedPair[];
  notInRnicDecisions: NotInRnicDecision[];
  rnicNotInFileDecisions: RnicNotInFileDecision[];
  dbCoprosNotInFileDecisions?: DbCoproNotInFileDecision[];
  syndicFeedbackOverrides?: SyndicFeedbackOverride[];
  lastUpdated?: string;
  status: 'in_progress' | 'completed';
}

type TabType = 'synthese' | 'manual';

/**
 * Detect if data is a comparison result (test import) or a regular import result.
 */
const isComparisonResult = (data: ImportDataSummary | null): boolean => {
  if (!data) {return false;}
  return 'summary' in data && 'coprosOk' in data;
};

/**
 * Detect if data is the stats produced by an applied (real) import:
 * `{ total, inserted, updated, ignored, errors, ... }`.
 */
const isImportResult = (data: ImportDataSummary | null): boolean => {
  if (!data) {return false;}
  return 'total' in data && 'inserted' in data && 'updated' in data;
};

/**
 * SCI contact imports and copro imports live side by side for a company:
 * "latest test import" and "already applied" only compare imports of the same family.
 */
const isSameImportFamily = (source: string | null, reference: string | null): boolean => (
  (source === IMPORT_SOURCES.SCI_EXCEL) === (reference === IMPORT_SOURCES.SCI_EXCEL)
);

const ImportDetail: React.FC = () => {
  const { importId } = useParams<{ importId: string }>();
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  
  const [importData, setImportData] = useState<Import | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('synthese');
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  
  // State for checking if this is the latest test import for this company
  const [latestTestImportIdForCompany, setLatestTestImportIdForCompany] = useState<number | null>(null);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);

  // State for tracking if this test import has already been applied as a real import
  const [hasBeenApplied, setHasBeenApplied] = useState(false);
  const [appliedImportId, setAppliedImportId] = useState<number | null>(null);
  const [applyingFeedback, setApplyingFeedback] = useState(false);

  const fetchImport = useCallback(async() => {
    if (!importId || !jwt) {return;}
    
    setLoading(true);
    try {
      const response = await fetch(`/api/imports/${importId}`, JWTHeader(jwt));
      
      if (!response.ok) {
        throw new Error('Failed to fetch import');
      }
      
      const data = await response.json();
      setImportData(data);
    } catch (error) {
      console.error('Error fetching import:', error);
      showToast('error', 'Erreur lors du chargement de l\'import.');
    } finally {
      setLoading(false);
    }
  }, [jwt, importId]);

  useEffect(() => {
    fetchImport();
  }, [fetchImport]);

  // Fetch the latest test import for this company to check if current import is the most recent
  const fetchLatestTestImportForCompany = useCallback(async() => {
    if (!jwt || !importData?.company?.id) {return;}
    
    try {
      const companyId = importData.company.id;
      const response = await fetch(`/api/imports?isTestImport=true&companyId=${companyId}`, JWTHeader(jwt));
      
      if (!response.ok) {
        throw new Error('Failed to fetch test imports for company');
      }
      
      const testImports: Import[] = (await response.json())
        .filter((testImport: Import) => isSameImportFamily(testImport.importSource, importData.importSource));
      // The API returns imports sorted by createdAt DESC, so the first one is the latest for this company
      if (testImports.length > 0) {
        setLatestTestImportIdForCompany(testImports[0].id);
      }
    } catch (error) {
      console.error('Error fetching latest test import for company:', error);
    }
  }, [jwt, importData?.company?.id, importData?.importSource]);

  useEffect(() => {
    fetchLatestTestImportForCompany();
  }, [fetchLatestTestImportForCompany]);

  // Check if this test import has already been applied (a real import exists for the same company created after it)
  const checkIfAlreadyApplied = useCallback(async() => {
    if (!jwt || !importData?.isTestImport || !importData?.company?.id) {
      setHasBeenApplied(false);
      setAppliedImportId(null);
      return;
    }

    try {
      const companyId = importData.company.id;
      const response = await fetch(
        `/api/imports?isTestImport=false&companyId=${companyId}`,
        JWTHeader(jwt),
      );

      if (!response.ok) {
        return;
      }

      const realImports: Import[] = await response.json();
      const testImportDate = new Date(importData.createdAt);
      const appliedImport = realImports.find(
        (ri) => isSameImportFamily(ri.importSource, importData.importSource) && new Date(ri.createdAt) > testImportDate,
      );

      if (appliedImport) {
        setHasBeenApplied(true);
        setAppliedImportId(appliedImport.id);
      } else {
        setHasBeenApplied(false);
        setAppliedImportId(null);
      }
    } catch (error) {
      console.error('Error checking if import has been applied:', error);
    }
  }, [jwt, importData?.isTestImport, importData?.company?.id, importData?.createdAt, importData?.importSource]);

  useEffect(() => {
    checkIfAlreadyApplied();
  }, [checkIfAlreadyApplied]);

  // Check if current import is the latest test import for this company
  const isLatestTestImportForCompany = importData?.isTestImport 
    ? (latestTestImportIdForCompany !== null && importData.id === latestTestImportIdForCompany)
    : true; // Non-test imports don't need this check

  const handleManualTabClick = () => {
    // If this is a test import that is not the latest for this company, show warning
    if (importData?.isTestImport && !isLatestTestImportForCompany) {
      setWarningDialogOpen(true);
      return;
    }
    setActiveTab('manual');
  };

  const handleWarningConfirm = () => {
    setWarningDialogOpen(false);
    setActiveTab('manual');
  };

  const handleWarningClose = () => {
    setWarningDialogOpen(false);
  };

  const handleSaveManualTreatment = async(manualTreatment: ManualTreatment | SciManualTreatment) => {
    if (!importId || !jwt) {return;}
    
    setSaving(true);
    try {
      const response = await fetch(`/api/imports/${importId}/manual-treatment`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...JWTHeader(jwt).headers,
        },
        body: JSON.stringify({ manualTreatment }),
      });

      if (!response.ok) {
        throw new Error('Failed to save manual treatment');
      }

      const updatedImport = await response.json();
      setImportData(updatedImport);
      showToast('success', 'Traitement manuel sauvegardé avec succès.');
    } catch (error) {
      console.error('Error saving manual treatment:', error);
      showToast('error', 'Erreur lors de la sauvegarde du traitement manuel.');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyImport = async() => {
    if (!importId || !jwt || !importData) {return;}
    
    setApplying(true);
    try {
      // Note: The ManualTreatmentTab already saves the current state before calling this function.
      // We don't need to save here as it would save stale parent state instead of the child's current state.

      // Apply the import
      const response = await fetch(`/api/imports/${importId}/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...JWTHeader(jwt).headers,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to apply import');
      }

      const newImport = await response.json();
      showToast('success', `Base réelle créée avec succès (ID: ${newImport.id}).`);
      
      // Navigate to the new import's detail page
      navigate(ROUTES.ADMIN_IMPORT_DETAIL.replace(':importId', String(newImport.id)));
    } catch (error) {
      console.error('Error applying import:', error);
      const message = error instanceof Error ? error.message : 'Erreur lors de l\'application de l\'import.';
      showToast('error', message);
    } finally {
      setApplying(false);
    }
  };

  const handleApplyFeedback = async() => {
    if (!importId || !jwt || !importData) {return;}

    setApplyingFeedback(true);
    try {
      const response = await fetch(`/api/imports/${importId}/apply-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...JWTHeader(jwt).headers,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to apply feedback');
      }

      const result = await response.json();
      showToast('success', `Modifications appliquées avec succès (${result.changesApplied ?? 0} changement(s)).`);

      // Refresh import data to get updated manual treatment
      await fetchImport();
    } catch (error) {
      console.error('Error applying feedback:', error);
      const message = error instanceof Error ? error.message : 'Erreur lors de l\'application des retours.';
      showToast('error', message);
    } finally {
      setApplyingFeedback(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) {return 'N/A';}
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const canShowManualTreatment = importData && isComparisonResult(importData.data);
  const isSciImport = importData?.importSource === IMPORT_SOURCES.SCI_EXCEL;

  if (loading) {
    return (
      <div className="mx-auto">
        <div className="rounded-lg bg-white p-6 shadow-md">
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
            <span className="ml-3 text-gray-600">Chargement de l'import...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!importData) {
    return (
      <div className="mx-auto">
        <div className="rounded-lg bg-white p-6 shadow-md">
          <div className="py-12 text-center">
            <Badge mode="error" text="Import non trouvé" />
            <button
              onClick={() => navigate(ROUTES.ADMIN_IMPORT_HISTORY)}
              className="mt-4 px-4 py-2 text-sm font-medium text-rgpd-blue-dark hover:underline"
            >
              Retour à l'historique
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto">
      {/* Warning Dialog for non-latest test import */}
      <NotLatestTestImport
        open={warningDialogOpen}
        onConfirm={handleWarningConfirm}
        onClose={handleWarningClose}
        context="manual-treatment"
      />

      {/* Header */}
      <div className="mb-8 rounded-lg bg-white p-6 shadow-md">
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() => navigate(ROUTES.ADMIN_IMPORT_HISTORY)}
            className="flex items-center gap-2 text-rgpd-blue-dark hover:underline"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            Retour à l'historique
          </button>
        </div>
        
        <h1 className="mb-4 flex items-center gap-3 text-2xl font-bold text-gray-800">
          <DocumentMagnifyingGlassIcon className="h-8 w-8 text-rgpd-blue-dark" />
          Détails de l'import #{importData.id}
          {importData.isTestImport && (
            <Badge mode="warning" text="Base tampon" />
          )}
        </h1>
        
        <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-4">
          <div>
            <span className="text-gray-500">Date :</span>
            <p className="font-medium">{formatDate(importData.createdAt)}</p>
          </div>
          <div>
            <span className="text-gray-500">Entreprise :</span>
            <p className="font-medium">{importData.company?.name || 'N/A'}</p>
            <p className="text-xs text-gray-400">{importData.company?.siren}</p>
          </div>
          <div>
            <span className="text-gray-500">Type :</span>
            <div className="mt-1">
              <Badge
                mode={getImportSourceBadgeMode(importData.importSource)}
                text={getImportSourceLabel(importData.importSource)}
              />
            </div>
          </div>
          <div>
            <span className="text-gray-500">Créé par :</span>
            <p className="font-medium">
              {importData.createdBy?.firstname} {importData.createdBy?.lastname}
            </p>
          </div>
        </div>

        {importData.isTestImport && (
          <div className="mt-4 flex items-center gap-2 text-sm">
            <span className="text-gray-500">Mode :</span>
            <span className="inline-flex items-center gap-1 text-orange-600">
              <XCircleIcon className="h-4 w-4" />
              Base tampon (aucune modification en base)
            </span>
          </div>
        )}
        {!importData.isTestImport && (
          <div className="mt-4 flex items-center gap-2 text-sm">
            <span className="text-gray-500">Mode :</span>
            <span className="inline-flex items-center gap-1 text-green-600">
              <CheckCircleIcon className="h-4 w-4" />
              Base réelle
            </span>
          </div>
        )}
      </div>

      {isSciImport && (
        <div className="rounded-lg bg-white p-6 shadow-md">
          {importData.isTestImport && isSciAnalysis(importData.data) ? (
            <SciImportReview
              analysis={importData.data}
              manualTreatment={importData.manualTreatment as unknown as SciManualTreatment | null}
              onSave={handleSaveManualTreatment}
              saving={saving}
              onApply={handleApplyImport}
              applying={applying}
              hasBeenApplied={hasBeenApplied}
              appliedImportId={appliedImportId}
              isLatestTestImport={isLatestTestImportForCompany}
            />
          ) : isSciReport(importData.data) ? (
            <SciImportResult report={importData.data} companyName={importData.company?.name ?? ''} />
          ) : (
            <pre className="max-h-96 overflow-auto rounded-lg bg-gray-100 p-4 text-xs">
              {JSON.stringify(importData.data, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* Tabs */}
      {!isSciImport && canShowManualTreatment && (
        <div className="mb-8">
          <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-gray-200/80 bg-gray-100">
            <button
              onClick={() => setActiveTab('synthese')}
              className={`flex min-h-28 cursor-pointer flex-col items-center justify-center gap-3 px-6 py-6 transition-all duration-300 ${
                activeTab === 'synthese'
                  ? 'border-b-8 border-rgpd-blue-dark bg-white font-bold text-slate-800 shadow-sm'
                  : 'border-b-4 border-transparent bg-transparent font-semibold text-slate-500 hover:bg-gray-50'
              }`}
            >
              <ClipboardDocumentListIcon className="h-8 w-8" />
              <span className="text-center text-base">Synthèse de l'import</span>
            </button>
            <button
              onClick={handleManualTabClick}
              className={`flex min-h-28 cursor-pointer flex-col items-center justify-center gap-3 px-6 py-6 transition-all duration-300 ${
                activeTab === 'manual'
                  ? 'border-b-8 border-rgpd-blue-dark bg-white font-bold text-slate-800 shadow-sm'
                  : 'border-b-4 border-transparent bg-transparent font-semibold text-slate-500 hover:bg-gray-50'
              }`}
            >
              <WrenchScrewdriverIcon className="h-8 w-8" />
              <span className="text-center text-base">Traitement manuel</span>
              {importData.manualTreatment && (
                <Badge
                  mode={importData.manualTreatment.status === 'completed' ? 'success' : 'warning'}
                  text={importData.manualTreatment.status === 'completed' ? 'Terminé' : 'En cours'}
                />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {!isSciImport && (
        <div className="rounded-lg bg-white p-6 shadow-md">
          {activeTab === 'synthese' && (
            <>
              {isComparisonResult(importData.data) ? (
                <ComparisonResultDisplay comparisonResult={importData.data as unknown as Parameters<typeof ComparisonResultDisplay>[0]['comparisonResult']} />
              ) : isImportResult(importData.data) ? (
                <ImportResultBody data={importData.data as unknown as ImportData} />
              ) : (
                <div className="p-4">
                  <p className="mb-4 text-gray-500">Structure de données non reconnue. Affichage brut :</p>
                  <pre className="max-h-96 overflow-auto rounded-lg bg-gray-100 p-4 text-xs">
                    {JSON.stringify(importData.data, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}

          {activeTab === 'manual' && canShowManualTreatment && (
            <ManualTreatmentTab
              comparisonData={importData.data as unknown as Parameters<typeof ManualTreatmentTab>[0]['comparisonData']}
              manualTreatment={importData.manualTreatment}
              onSave={handleSaveManualTreatment}
              saving={saving}
              onApplyImport={handleApplyImport}
              applying={applying}
              hasOriginalFile={!!importData.originalFileName}
              jwt={jwt}
              importMeta={{
                id: importData.id,
                companyId: importData.company?.id,
                companyName: importData.company?.name,
                fileName: importData.originalFileName,
                createdAt: importData.createdAt,
              }}
              hasBeenApplied={hasBeenApplied}
              appliedImportId={appliedImportId}
              onApplyFeedback={handleApplyFeedback}
              applyingFeedback={applyingFeedback}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ImportDetail;

